import styled from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  page: "#f5f7fc",
  white: "#ffffff",
  title: "#293142",
  muted: "#7d8599",
  lightMuted: "#a7afbf",
  border: "#e1e6ef",
  brand: "#ef4f5f",
  input: "#f8f9fc",
  info: "#fff2e4",
};

const MOBILE = "@media (max-width: 480px)";
const PAGE_MAX_WIDTH = "760px";

export const InvoiceEditorShell = styled.main`
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: ${COLORS.page};
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
`;

export const InvoiceEditorFrame = styled.div`
  display: flex;
  width: min(100%, ${PAGE_MAX_WIDTH});
  height: 100%;
  flex-direction: column;
  margin: 0 auto;
  background: ${COLORS.page};
`;

export const InvoiceEditorHeader = styled.header`
  display: flex;
  min-height: 72px;
  flex: 0 0 auto;
  align-items: center;
  gap: 14px;
  padding: 12px 24px;
  background: ${COLORS.white};

  ${MOBILE} {
    min-height: 62px;
    padding: 10px 16px;
  }
`;

export const InvoiceEditorHeaderCopy = styled.div`
  min-width: 0;

  h1 {
    overflow: hidden;
    margin: 0;
    color: ${COLORS.title};
    font-family: ${fontFamilies.bold};
    font-size: 16px;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 4px;
    color: ${COLORS.muted};
    font-size: 12px;
  }

  ${MOBILE} {
    h1 {
      font-size: 15px;
    }

    span {
      font-size: 11px;
    }
  }
`;

export const InvoiceEditorScrollArea = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const InvoiceEditorContent = styled.div`
  padding: 24px 28px 32px;

  ${MOBILE} {
    padding: 18px 18px 26px;
  }
`;

export const AddInvoiceButton = styled.button`
  display: flex;
  width: 100%;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1.5px dashed ${COLORS.brand};
  border-radius: 13px;
  background: ${COLORS.white};
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
    fill: ${COLORS.brand};
    stroke: ${COLORS.white};
  }

  &:active {
    transform: translateY(1px);
  }

  ${MOBILE} {
    min-height: 44px;
    border-radius: 11px;
    font-size: 13px;
  }
`;

export const EditorInvoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`;

export const NewInvoiceCard = styled.section`
  overflow: hidden;
  border-radius: 18px;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.07);
`;

export const NewInvoiceHeader = styled.div`
  display: flex;
  min-height: 50px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px 8px;

  h2 {
    margin: 0;
    font-family: ${fontFamilies.bold};
    font-size: 16px;
  }

  ${MOBILE} {
    min-height: 44px;
    padding: 8px 14px 6px;

    h2 {
      font-size: 15px;
    }
  }
`;

export const DeleteIconButton = styled.button`
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.border};
  border-radius: 50%;
  padding: 0;
  background: ${COLORS.white};
  color: ${COLORS.brand};
  cursor: pointer;

  svg {
    width: 21px;
    height: 21px;
  }

  &:active {
    transform: scale(0.97);
  }

  ${MOBILE} {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
  }
`;

export const UploadArea = styled.div`
  padding: 18px 20px 8px;

  ${MOBILE} {
    padding: 14px 14px 6px;
  }
`;

export const UploadOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
`;

export const UploadOptionButton = styled.button`
  display: flex;
  min-height: 104px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  border: 1.5px dashed ${COLORS.brand};
  border-radius: 12px;
  padding: 12px;
  background: #fff5f5;
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;
  cursor: pointer;

  svg {
    width: 28px;
    height: 28px;
  }

  ${MOBILE} {
    min-height: 90px;
    font-size: 12px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  ${MOBILE} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }
`;

export const ImagePreview = styled.div`
  position: relative;
  aspect-ratio: 1;
  min-width: 0;

  img {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 10px;
    object-fit: cover;
  }
`;

export const RemoveFileButton = styled.button`
  position: absolute;
  top: -7px;
  right: -7px;
  display: inline-flex;
  width: 23px;
  height: 23px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  padding: 0;
  background: #aeb5c4;
  color: ${COLORS.white};
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const AddImageTile = styled.button`
  display: flex;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  border: 1.5px dashed ${COLORS.brand};
  border-radius: 10px;
  padding: 8px;
  background: #fffafb;
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 12px;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const PdfPreview = styled.div`
  position: relative;
  display: inline-flex;
  width: 120px;
  min-height: 100px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  padding: 12px 9px;
  background: ${COLORS.white};
  color: ${COLORS.title};
  text-align: center;

  > svg {
    width: 28px;
    height: 28px;
    color: ${COLORS.brand};
  }

  span {
    display: -webkit-box;
    overflow: hidden;
    font-family: ${fontFamilies.semiBold};
    font-size: 12px;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  ${MOBILE} {
    width: 104px;
    min-height: 92px;
  }
`;

export const InvoiceFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  padding: 18px 20px 22px;

  ${MOBILE} {
    gap: 14px 10px;
    padding: 16px 14px 18px;
  }
`;

export const FieldWrapper = styled.label`
  position: relative;
  display: block;
  min-width: 0;
`;

export const FieldLabel = styled.span`
  position: absolute;
  z-index: 1;
  top: -8px;
  left: 12px;
  padding: 0 5px;
  background: ${COLORS.white};
  color: ${(props) => (props.$error ? COLORS.brand : COLORS.lightMuted)};
  font-size: 12px;
`;

export const FieldInput = styled.input`
  width: 100%;
  min-width: 0;
  min-height: 46px;
  border: 1px solid ${(props) => (props.$error ? COLORS.brand : COLORS.border)};
  border-radius: 11px;
  outline: 0;
  padding: 10px 12px;
  padding-right: 32px;
  box-sizing: border-box;
  background: ${COLORS.white};
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
  font-size: 14px;

  &::placeholder {
    color: ${COLORS.lightMuted};
  }

  ${MOBILE} {
    min-height: 42px;
    padding: 9px 10px;
    padding-right: 28px;
    font-size: 13px;
  }
`;

export const FieldSuffix = styled.span`
  position: absolute;
  top: 50%;
  right: 12px;
  color: ${COLORS.lightMuted};
  font-size: 13px;
  transform: translateY(-50%);
  pointer-events: none;
`;

export const CardError = styled.p`
  margin: 0;
  padding: 0 20px 18px;
  color: ${COLORS.brand};
  font-size: 13px;

  ${MOBILE} {
    padding: 0 14px 16px;
    font-size: 12px;
  }
`;

export const ExistingInvoiceCard = styled.article`
  display: grid;
  min-width: 0;
  grid-template-columns: 60px minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  border-radius: 17px;
  padding: 16px;
  background: ${COLORS.white};
  box-shadow: 0 3px 11px rgba(41, 49, 66, 0.07);

  ${MOBILE} {
    grid-template-columns: 52px minmax(0, 1fr) auto;
    gap: 12px;
    border-radius: 14px;
    padding: 13px;
  }
`;

export const ExistingInvoiceThumbnail = styled.a`
  position: relative;
  display: block;
  width: 60px;
  height: 60px;
  overflow: hidden;
  border-radius: 8px;
  background: ${COLORS.input};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  > svg {
    position: absolute;
    right: 4px;
    bottom: 4px;
    width: 14px;
    height: 14px;
    padding: 2px;
    border-radius: 3px;
    background: rgba(41, 49, 66, 0.6);
    stroke-width: 1.9;
    color: white;
    stroke: white;
  }

  /* react-pdf canvas thumbnail */
  .react-pdf__Document,
  .react-pdf__Page,
  .react-pdf__Page__canvas {
    display: block;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }

  .react-pdf__Page__canvas {
    border-radius: 8px;
  }

  ${MOBILE} {
    width: 52px;
    height: 52px;

    .react-pdf__Page__canvas {
      border-radius: 8px;
    }

    > svg {
      width: 12px;
      height: 12px;
    }
  }
`;

export const ExistingInvoiceDocumentFallback = styled.span`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #7d8599;
`;

export const ExistingInvoiceCopy = styled.div`
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-family: ${fontFamilies.bold};
    font-size: 17px;
  }

  span {
    margin-top: 6px;
    color: ${COLORS.muted};
    font-family: ${fontFamilies.semiBold};
    font-size: 13px;
  }

  ${MOBILE} {
    strong {
      font-size: 14px;
    }

    span {
      margin-top: 4px;
      font-size: 11px;
    }
  }
`;

export const InvoiceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  border-radius: 11px;
  padding: 14px 16px;
  background: ${COLORS.info};
  color: #596174;
  font-size: 14px;

  svg {
    width: 19px;
    height: 19px;
    flex: 0 0 19px;
  }

  ${MOBILE} {
    padding: 12px;
    font-size: 12px;
  }
`;

export const PageError = styled.p`
  margin: 16px 0 0;
  color: ${COLORS.brand};
  font-size: 14px;
  text-align: center;
`;

export const InvoiceEditorFooter = styled.footer`
  flex: 0 0 auto;
  border-top: 1px solid #edf0f5;
  padding: 18px 28px 24px;
  background: ${COLORS.white};
  box-shadow: 0 -8px 24px rgba(41, 49, 66, 0.05);

  ${MOBILE} {
    padding: 14px 18px 18px;
  }
`;

export const SubmitInvoicesButton = styled.button`
  width: 100%;
  min-height: 58px;
  border: 0;
  border-radius: 14px;
  padding: 0 20px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 19px;
  cursor: pointer;

  &:disabled {
    background: #cfd4de;
    cursor: default;
  }

  ${MOBILE} {
    min-height: 52px;
    border-radius: 12px;
    font-size: 16px;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: ${(props) => (props.$sheet ? "flex-end" : "center")};
  justify-content: center;
  padding: ${(props) => (props.$sheet ? "0" : "24px")};
  background: rgba(0, 0, 0, 0.55);
`;

export const ModalSheet = styled.div`
  width: min(100%, 760px);
  border-radius: 24px 24px 0 0;
  padding: 20px 20px 28px;
  background: #f5f7fc;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  h2 {
    margin: 14px 0 0;
    font-family: ${fontFamilies.bold};
    font-size: 20px;
    line-height: 1.25;
  }

  p {
    margin: 10px 0 0;
    color: ${COLORS.muted};
    font-size: 13px;
    line-height: 1.4;
  }

  ${MOBILE} {
    padding: 16px 14px 24px;

    h2 {
      font-size: 18px;
    }
  }
`;

export const ModalCard = styled.div`
  width: min(100%, 390px);
  border-radius: 20px;
  padding: 28px 24px 24px;
  background: ${COLORS.white};
  text-align: center;

  h2 {
    margin: 18px 0 0;
    font-family: ${fontFamilies.bold};
    font-size: 21px;
    line-height: 1.25;
  }

  p {
    margin: 14px 0 0;
    color: ${COLORS.muted};
    font-size: 14px;
    line-height: 1.4;
  }

  ${MOBILE} {
    border-radius: 18px;
    padding: 24px 18px 20px;

    h2 {
      font-size: 18px;
    }
  }
`;

export const ModalIcon = styled.span`
  display: inline-flex;
  width: 70px;
  height: 70px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${(props) => (props.$success ? "#e9f8ef" : "#fff0e4")};
  color: ${(props) => (props.$success ? "#159553" : "#d7551d")};

  svg {
    width: 40px;
    height: 40px;
  }

  img {
    width: 36px;
    height: 36px;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.$horizontal ? "row" : "column")};
  gap: 12px;
  margin-top: 20px;
  width: 100%;
  max-width: 480px;
`;

export const SheetButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: calc(100% + 40px);
  margin: 16px -20px -28px;
  padding: 14px 20px 28px;
  background: ${COLORS.white};
  border-radius: 0 0 24px 24px;

  ${MOBILE} {
    width: calc(100% + 28px);
    margin: 14px -14px -24px;
    padding: 12px 14px 24px;
    gap: 10px;
  }
`;

export const ModalPrimaryButton = styled.button`
  min-height: 48px;
  flex: 1;
  border: 1px solid ${COLORS.brand};
  border-radius: 11px;
  padding: 0 16px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;
  cursor: pointer;
`;

export const ModalSecondaryButton = styled.button`
  min-height: 44px;
  flex: 1;
  border: ${(props) => (props.$outlined ? `1px solid ${COLORS.brand}` : "0")};
  border-radius: 11px;
  padding: 0 16px;
  background: ${COLORS.white};
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;
  cursor: pointer;
`;
